import React, { useEffect } from 'react'
import Select, { components } from 'react-select'
import { LogsFilterSortedDown, LogsFilterSortedUp, NavBarSortedDown, NavBarSortedUp } from './ProjectIcons';
import { activityLogStyle, connectionFilterStyle, defaultStyle, languageSelectStyle, navLanguageMobileStyle, navTimezoneStyle, navTimezoneMobileStyle, timezoneStyle } from './componentstyles/selectinputstyles';

export type SelectVariant = 'default' | 'language' | 'timezone' | 'activityLog' | 'connectionFilter' | 'navTimezone' | 'navTimezoneMobile' | 'navLanguageMobile';

type SelectInputProps = {
    options: Array<any>,
    className: string,
    defaultValue?: object,
    value?: object | null,
    isDisabled: boolean,
    isLoading: boolean,
    isClearable: boolean,
    isSearchable: boolean,
    isMulti: boolean,
    name: string,
    onFocus?: () => void,
    onChange: Function,
    onMenuScrollToBottom?: any,
    handleInputChange?: any,
    variant?: SelectVariant,
    languageSelect?: boolean,// @deprecated Use variant="language" instead
    timezoneSelect?: boolean, // @deprecated Use variant="timezone" instead
    components?: any,
    onMenuOpen?: () => void,
    onMenuClose?: () => void,
    keepOptionsOrder?: boolean,
    onKeyDown?: (e: React.KeyboardEvent) => void,
    noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode,
    autoSelectSingleOption?: boolean,
    menuPortalTarget?: HTMLElement | null,
    menuPosition?: 'fixed' | 'absolute',
    inputId?: string,
    formatOptionLabel?: (option: { label: string; value: string; [key: string]: any }) => React.ReactNode,
}

const CustomOption = (props: any) => {
    const { data, innerRef, innerProps } = props;
    return (
        <div ref={innerRef} {...innerProps} className='p-3 flex flex-col hover:bg-light-blue-hover w-full whitespace-nowrap'>
            <div className='text-off-black'>{data.label}</div>
            <div className='text-secondary-gray'>{data.subtitle}</div>
        </div>
    );
};

const CustomSingleValue = (props: any) => {
    const { data } = props;
    return (
        <components.SingleValue {...props}>
            <span>{data.label}</span>
        </components.SingleValue>
    );
};

const NavDropdownIndicator = (props: any) => {
    const { selectProps } = props;
    const className = 'text-black';

    return (
        <components.DropdownIndicator {...props} className={className}>
            {selectProps.menuIsOpen ? <NavBarSortedUp /> : <NavBarSortedDown />}
        </components.DropdownIndicator>
    );
};

const DefaultDropdownIndicator = (props: any) => {
    const { selectProps } = props;

    return (
        <components.DropdownIndicator {...props}>
            {selectProps.menuIsOpen ? <LogsFilterSortedUp /> : <LogsFilterSortedDown />}
        </components.DropdownIndicator>
    );
};

const SelectInput = ({
    options,
    className,
    defaultValue,
    value,
    isDisabled,
    isLoading,
    isClearable,
    isSearchable,
    isMulti,
    name,
    onFocus,
    onChange,
    onMenuScrollToBottom,
    handleInputChange,
    variant = 'default',
    languageSelect,
    timezoneSelect,
    keepOptionsOrder = false,
    onKeyDown,
    noOptionsMessage,
    autoSelectSingleOption = false,
    menuPortalTarget: menuPortalTargetProp,
    menuPosition: menuPositionProp,
    inputId,
    formatOptionLabel,
}: SelectInputProps) => {
    // For backward compatibility
    if (languageSelect) variant = 'language';
    if (timezoneSelect) variant = 'timezone';

    const isSingleOption = autoSelectSingleOption && options.length === 1;

    useEffect(() => {
        if (isSingleOption && value !== options[0]) {
            onChange(options[0]);
        }
    }, [isSingleOption, options]);

    // Get styles based on variant
    const getStylesForVariant = () => {
        switch (variant) {
            case 'timezone': return timezoneStyle;
            case 'language': return languageSelectStyle;
            case 'activityLog': return activityLogStyle;
            case 'connectionFilter': return connectionFilterStyle;
            case 'navTimezone': return navTimezoneStyle;
            case 'navTimezoneMobile': return navTimezoneMobileStyle;
            case 'navLanguageMobile': return navLanguageMobileStyle;
            default: return defaultStyle;
        }
    };

    const styles = getStylesForVariant();

    // Function to reorder options
    const reorderOptions = (options: any[], selectedValue: any) => {
        if (!selectedValue || keepOptionsOrder) return options;
        const selectedIndex = options.findIndex(option => option.label === selectedValue.label);
        if (selectedIndex === -1) return options;
        const [selectedOption] = options.splice(selectedIndex, 1);
        return [selectedOption, ...options];
    };

    // Reorder options based on the selected value
    const reorderedOptions = reorderOptions([...options], value);

    // Define custom components outside the getComponentsForVariant function
    const DefaultOption = (props: any) => {
        const { children, ...rest } = props;
        return (
            <components.Option {...rest}>
                {children}
            </components.Option>
        );
    };

    const DefaultSingleValue = (props: any) => {
        const { children, ...rest } = props;
        return (
            <components.SingleValue {...rest}>
                {children}
            </components.SingleValue>
        );
    };

    // Get components based on variant
    const getComponentsForVariant = () => {
        const baseComponents: any = {};

        // Add DropdownIndicator for specific variants
        if (['language', 'timezone', 'navTimezone', 'navTimezoneMobile', 'navLanguageMobile'].includes(variant)) {
            baseComponents.DropdownIndicator = NavDropdownIndicator;
        }

        // Add CustomOption and CustomSingleValue for specific variants
        if (['timezone', 'activityLog', 'navTimezone', 'navTimezoneMobile'].includes(variant)) {
            baseComponents.Option = CustomOption;
            baseComponents.SingleValue = CustomSingleValue;
        } else {
            // For variants that don't have custom option components
            baseComponents.Option = DefaultOption;
            baseComponents.SingleValue = DefaultSingleValue;
        }

        if (['activityLog'].includes(variant)) {
            baseComponents.DropdownIndicator = DefaultDropdownIndicator;
        }

        return baseComponents;
    };

    return (
        <Select
            placeholder=""
            inputId={inputId}
            options={reorderedOptions} // Use reordered options here
            className={className}
            defaultValue={defaultValue}
            value={isSingleOption ? options[0] : value}
            isDisabled={isDisabled || isSingleOption}
            isLoading={isLoading}
            isClearable={isClearable}
            isSearchable={isSearchable}
            isMulti={isMulti}
            name={name}
            onFocus={onFocus}
            onChange={(e) => onChange(e)}
            onMenuScrollToBottom={onMenuScrollToBottom}
            onInputChange={handleInputChange}
            styles={styles}
            components={getComponentsForVariant()}
            onKeyDown={onKeyDown}
            noOptionsMessage={noOptionsMessage}
            formatOptionLabel={formatOptionLabel as any}
            menuPortalTarget={menuPortalTargetProp !== undefined ? menuPortalTargetProp : (variant === 'connectionFilter' && typeof document !== 'undefined' ? document.body : undefined)}
            menuPosition={menuPositionProp ?? (variant === 'connectionFilter' ? 'fixed' : undefined)}
            filterOption={(option: any, input) => {
                if (!input) return true;
                const lowerCaseInput = input.toLowerCase().trim();
                const searchableFields = [
                    option.label.toLowerCase(),
                    ...(option.data.searchTerms || [])
                ];

                return searchableFields.some(field =>
                    field.includes(lowerCaseInput) ||
                    lowerCaseInput.split(" ").every((word: string) => field.includes(word))
                );
            }}
        />
    );
};

export default SelectInput;